import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

const BazzAIStudio = ({ content, onUpdate, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [loadingAction, setLoadingAction] = useState(null);

  const actions = [
    { key: 'grammar', label: t('ai.grammar', 'Fix Grammar & Style'), emoji: '🪄' },
    { key: 'darija', label: t('ai.darija', 'Convert to Darija'), emoji: '🇲🇦' },
    { key: 'punchy', label: t('ai.punchy', 'Make it Punchier'), emoji: '🎭' },
    { key: 'condense', label: t('ai.condense', 'Condense to 280'), emoji: '✂️' },
  ];

  const handleAction = async (action) => {
    if (!content || !content.trim()) {
      toast.error('Write something first!');
      return;
    }
    if (loadingAction) return;

    setLoadingAction(action);
    try {
      const res = await axios.post('/api/ai/polish', { content, action });
      const result = res.data?.content;
      if (result) {
        onUpdate(result);
        toast.success('Content updated by AI ✨');
      } else {
        toast.error('AI returned an empty response');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'AI request failed';
      toast.error(msg);
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <>
      {/* Backdrop (invisible click-away) */}
      <div
        className="fixed inset-0 z-[19]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="absolute left-0 bottom-full mb-2 z-20 w-64 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-zinc-100 dark:border-zinc-800">
          <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
            ✨ {t('ai.studioTitle', 'AI Creator Studio')}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
            aria-label="Close AI Studio"
          >
            <i className="fas fa-times text-xs" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="py-1.5">
          {actions.map(({ key, label, emoji }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleAction(key)}
              disabled={loadingAction !== null}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left text-xs sm:text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-60"
            >
              {loadingAction === key ? (
                <span className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin shrink-0" />
              ) : (
                <span className="text-base leading-none shrink-0">{emoji}</span>
              )}
              <span className={loadingAction === key ? 'text-violet-600 dark:text-violet-400' : ''}>
                {loadingAction === key ? t('ai.polishing', 'Processing…') : label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default BazzAIStudio;
